import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import AssessmentListTable from './AssessmentListTable';
import ScormAssessmentListTable from './ScormAssessmentListTable';
import ThemedSpinner from '../../../components/themed/ThemedSpinner';
import { useStores } from '../../../hooks/use-stores';
import Modal from 'react-modal';
import FilterModal from '../../../components/manager/filter-modal';
import { Link } from 'react-router-dom';
import { ThemedNav, ThemedNavItem, ThemedNavLink } from '../../../components/themed/ThemedComponents';
import { MenuItem } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import './AssessmentList.scss';
import Snackbar from '../../../components/Snackbar';
import ErrorMessage from '../../../components/errormessage';
import NoItemsYet from '../../../components/manager/no-items-yet';
import {
    Block,
    FlexDiv,
    Heading3,
    palette,
    SpaceBetweenDiv,
    TextDefault,
    TextInput,
    TextTiny,
    ThemedButton,
} from '@awarego-ui/components';
import NoItemsYetContent from '../../../components/manager/no-items-yet-content';
import RequireRole from '../../RequireRole';
import HeaderSection from '../../../components/header/HeaderSection';
import CreateAssessmentButton from '../../../components/CreateAssessmentButton';
import { useAssessmentType } from '../useAssessmentType';

function AssessmentList({ companyId, tab = 'all' }) {
    const {store, listStore, commonStore, cmi5Store, brandingStore } =
        useStores();
    const [filterModal, setFilterModal] = useState(false);

    const {
        assessmentStore,
        isAssessmentInsight,
        parentRoute,
        name,
        namePlural,
        title,
        titlePlural,
        noItemsDescription,
        deliveryDate
    } = useAssessmentType()

    const {
        insightsHeaderTitle
    } = brandingStore

    const {
        loadingAssessments,
        loadingAssessmentsInBackground,
        assessments,
        searchValue,
        areaFilter,
        employeeFilter,
        sortBy,
        filters,
        filteredAssessments,
        isBothAssessmentTypes,
        isScormOnly,
        scormAssessments,
        filteredScormAssessments,
        startDueDateFilter,
        endDueDateFilter,
        startDeliveryDateFilter,
        endDeliveryDateFilter,
        error,
        demoAssessmentURL,
    } = assessmentStore;
    const { cmi5enabled } = store;
    const { listsWithUsers: lists } = listStore;

    const [message, setMesage] = useState(null);
    const [messageShown, setMessageShown] = useState(false);
    const [messageType, setMessageType] = useState(null);

    useEffect(() => {
        assessmentStore.loadAssessments(companyId);
        if(!isAssessmentInsight) {
            listStore.loadCompanyLists(companyId);
        }
    }, [companyId]);

    const filterCount = [
        areaFilter.length ? 1 : 0,
        employeeFilter.length ? 1 : 0,
        startDueDateFilter !== null && endDueDateFilter !== null ? 1 : 0,
        startDeliveryDateFilter !== null && endDeliveryDateFilter !== null
            ? 1
            : 0,
    ].reduce((a, b) => a + b);

    const hideFilterModal = () => {
        setFilterModal(false);
    };

    const showFilterModal = () => {
        setFilterModal(true);
    };

    const onDelete = (id, isScorm) => {
        commonStore.showConfirm(
            `${title} will be deleted forever.`,
            'Yes, delete',
            `Delete this ${name}?`,
            async () => {
                const result = await assessmentStore.delete(
                    companyId,
                    isScorm ? `p${id}` : id
                );
                if (result) showSnackBar(`${title}  deleted!`);
            },
            true
        );
    };

    const onDownloadPackage = (id, uniqueId, type) => {
        cmi5Store.downloadZIP(companyId, id, uniqueId, type);
    };

    const onPause = (id) => {
        commonStore.showConfirm(
            `Are you sure you want to stop this ${name}?`,
            'Stop',
            `Stop ${name}?`,
            async () => {
                await pauseAssessment(id);
                showSnackBar(`${title} paused!`);
            },
            false
        );
    };

    const pauseAssessment = async (id) => {
        await assessmentStore.pauseAssessment(companyId, id);
    };

    const showSnackBar = (message, messageType = 'success') => {
        setMesage(message);
        setMessageType(messageType);
        setMessageShown(true);
    };

    const closeSnackbar = () => {
        setMessageShown(false);
        setMesage(null);
        setMessageType(null);
    };

    const tabs = useMemo(() => {
        return [
            {
                code: 'all',
                title: 'All',
            },
            {
                code: 'active',
                title: 'Active',
            },
            {
                code: 'completed',
                title: 'Completed',
            },
            {
                code: 'draft',
                title: 'Draft',
                visible: store.isAdmin,
            },
        ];
    }, [store.isAdmin]);

    const visibleTabs = useMemo(() => {
        return tabs.filter((tab) => tab.visible !== false);
    }, [tabs]);

    const sortOptions = [
        {
            value: '_rawData.last_updated',
            text: 'Last updated',
        },
        {
            value: 'deliveryDate',
            text: deliveryDate
        },
        {
            value: '_rawData.due',
            text: 'Due date',
        },
    ];

    const addHeaderSection = () => {
        return (
            <HeaderSection
                heading={isAssessmentInsight ? insightsHeaderTitle : titlePlural}
                searchPlaceholder={'Search by name'}
                searchValue={searchValue || ''}
                onSearch={(e) => assessmentStore.setSearchValue(e.target.value)}
                onClearSearch={() => assessmentStore.setSearchValue('')}
                onShowFilter={showFilterModal}
                filterCount={filterCount}
                onClearFilters={() => assessmentStore.clearFilters()}
                buttons={<CreateAssessmentButton cmi5enabled={cmi5enabled} isAssessmentInsight={isAssessmentInsight}/>}
            />
        );
    };
    const getTabCount = (t) => {
        if (t.code === 'all' && isBothAssessmentTypes) return;
        const count = assessmentStore[`${t.code}Tab`].length;
        return (
            <TextTiny
                bold
                padding={4}
                backgroundColor={palette.graphite.gray}
                color={palette.graphite.charcoal}
                ml={8}
            >
                {count}
            </TextTiny>
        );
    };

    return loadingAssessments ? (
        <>
            {addHeaderSection()}
            <ThemedSpinner />
        </>
    ) : (
        <Fragment>
            <ErrorMessage error={error} />
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                autoHideDuration={5000}
                variant={messageType}
                message={message || ''}
                open={messageShown}
                onClose={closeSnackbar}
            />
            {/* We should move this to specific component and use <Outlet/> here */}
            {assessments.length > 0 && (
                <Fragment>
                    {addHeaderSection()}
                    {!isScormOnly && (
                        <Block gap={32} tablePadding>
                            {isBothAssessmentTypes && (
                                <Heading3 ml={16}>
                                    Regular assessments (
                                    {filteredAssessments.length})
                                </Heading3>
                            )}

                            <SpaceBetweenDiv positionRelative ml={16} pr={32}>
                                {loadingAssessmentsInBackground && (
                                    <CircularProgress
                                        size={15}
                                        className={'background-loader'}
                                        color="primary"
                                    />
                                )}
                                <ThemedNav clean>
                                    {visibleTabs.map((t) => (
                                        <ThemedNavItem
                                            active={tab === t.code}
                                            key={t.code}
                                        >
                                            <ThemedNavLink
                                                to={`${parentRoute}/${t.code}`}
                                                as={Link}
                                            >
                                                <FlexDiv alignCenter>
                                                    {t.title} {getTabCount(t)}
                                                </FlexDiv>
                                            </ThemedNavLink>
                                        </ThemedNavItem>
                                    ))}
                                </ThemedNav>
                                <FlexDiv alignCenter gap={8}>
                                    <TextDefault lighter>Sort by:</TextDefault>
                                    <TextInput
                                        select
                                        inline
                                        value={sortBy}
                                        onChange={(e) => {
                                            assessmentStore.setSortBy(
                                                e.target.value
                                            );
                                        }}
                                    >
                                        {sortOptions.map((p) => (
                                            <MenuItem
                                                key={p.value}
                                                value={p.value}
                                            >
                                                {p.text}
                                            </MenuItem>
                                        ))}
                                    </TextInput>
                                </FlexDiv>
                            </SpaceBetweenDiv>
                            {assessmentStore[`${tab}Tab`].length > 0 && (
                                <AssessmentListTable
                                    assessments={[
                                        ...assessmentStore[`${tab}Tab`],
                                    ]}
                                    onDelete={onDelete}
                                    lists={lists}
                                    defaultSortBy={sortBy}
                                />
                            )}
                            {assessmentStore[`${tab}Tab`].length === 0 && (
                                <NoItemsYetContent
                                    img={'/static/img/icon-booklist.svg'}
                                    imgAlt={`No ${name} found`}
                                    title={`No ${name} found`}
                                    text={
                                        'Try adjusting your search or filter to find what you were looking for.'
                                    }
                                />
                            )}
                        </Block>
                    )}
                </Fragment>
            )}
            {scormAssessments.length > 0 && (
                <ScormAssessmentListTable
                    assessments={filteredScormAssessments}
                    onDelete={onDelete}
                    onDownloadPackage={onDownloadPackage}
                    defaultSortBy={sortBy}
                    isPackageDownloading={cmi5Store.isZIPDownloading}
                    isBothAssessmentTypes={isBothAssessmentTypes}
                />
            )}
            {assessments.length === 0 && (
                <NoItemsYet
                    img={'/static/img/assessment-empty.svg'}
                    imgAlt={'sneak peak into assessment'}
                    title={`No ${namePlural} yet`}
                    text={noItemsDescription}
                    heading={titlePlural}
                    buttons={
                        <FlexDiv gap={24}>
                            <RequireRole>{<CreateAssessmentButton cmi5enabled={cmi5enabled} isAssessmentInsight={isAssessmentInsight}/>}</RequireRole>
                            {!isAssessmentInsight && (
                            <ThemedButton
                                as="a"
                                href={demoAssessmentURL}
                                target="_blank"
                                rel="noreferrer"
                                mode="secondary"
                                text="Try demo"
                            />
                            )}
                        </FlexDiv>
                    }
                />
            )}
            <Modal
                isOpen={filterModal}
                onRequestClose={hideFilterModal}
                className="filter-modal"
                ariaHideApp={false}
            >
                <FilterModal
                    onRequestClose={hideFilterModal}
                    resultCount={
                        assessmentStore[`${tab}Tab`].length + filteredScormAssessments.length
                    }
                    filterCount={filterCount}
                    clearFilters={() => assessmentStore.clearFilters()}
                    hideDateFilters={isScormOnly}
                    filters={filters}
                />
            </Modal>
        </Fragment>
    );
}

export default observer(AssessmentList);
